import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { CustomFieldDefinition } from '../types/customField';

export const useCustomFields = (applicableTo?: string) => {
  const { user } = useAuth();
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Betöltés
  useEffect(() => {
    if (!user?.organizationId) {
      setCustomFields([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let q = query(
      collection(db, 'customFields'),
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fields = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as CustomFieldDefinition;
        });

        // Szűrés applicableTo alapján, ha meg van adva
        const filteredFields = applicableTo 
          ? fields.filter(field => field.applicableTo.includes(applicableTo as any))
          : fields;

        setCustomFields(filteredFields);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading custom fields:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId, applicableTo]);

  // Új custom field létrehozása
  const createCustomField = useCallback(async (data: Omit<CustomFieldDefinition, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy'>) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    const customFieldData = {
      ...data,
      organizationId: user.organizationId,
      createdBy: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'customFields'), customFieldData);
    return docRef.id;
  }, [user]);

  // Custom field frissítése
  const updateCustomField = useCallback(async (
    fieldId: string, 
    updates: Partial<Omit<CustomFieldDefinition, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy'>>
  ) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    await updateDoc(doc(db, 'customFields', fieldId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }, [user]);

  // Custom field törlése
  const deleteCustomField = useCallback(async (fieldId: string) => {
    if (!user?.organizationId) throw new Error('No organization ID');

    await deleteDoc(doc(db, 'customFields', fieldId));
  }, [user]);

  return {
    customFields,
    loading,
    error,
    createCustomField,
    updateCustomField,
    deleteCustomField
  };
};
